import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { UserProfile, UserProfileResponse } from '../../../types/commons';
import { RuntimeConfigService } from '../common/runtime-config.service';
import { SKIP_GLOBAL_ERROR_REDIRECT } from '../../utils/http-context-tokens.util';

/**
 * Read-only: `GET /api/user/profile`, open to any authenticated user.
 *
 * Since 2026-10-20 the response carries the caller's own company NAME only
 * (`company`, `null` for a SuperAdmin) and no `companies` list: anything that
 * needs a company list reads it from `GET /api/user/users` instead (see
 * `UserProfile` in `src/types/commons.ts`).
 *
 * Writing a profile is not this service's job — the two halves belong to their
 * own owners:
 *
 *   - password  -> `AuthService.changePassword()`      (any authenticated user)
 *   - profile   -> `UserManagementService.updateUser()` (Admin or SuperAdmin)
 */
@Injectable({
  providedIn: 'root',
})
export class ProfileService {
  private readonly config = inject(RuntimeConfigService);
  private readonly API_URL = `${this.config.apiUrl()}/user`;

  constructor(private http: HttpClient) {}

  fetchProfileData(): Observable<UserProfile> {
    return this.http
      .get<UserProfileResponse>(`${this.API_URL}/profile`, {
        withCredentials: true,
        // See SKIP_GLOBAL_ERROR_REDIRECT's own docblock: this call's failure
        // is classified and toasted by page-load-failure.util.ts (the
        // PROFILE and USER_MGMT_SELF scopes) — the global interceptor's
        // redirect must not win that race.
        context: new HttpContext().set(SKIP_GLOBAL_ERROR_REDIRECT, true),
      })
      .pipe(map((response) => response.data));
  }
}
